import { api } from '@src/utils/axios';
import {
  generateThumbnail,
  generateThumbnailFilename,
} from '@src/utils/image-utils';

export interface ImageResponse {
  id: number;
  url: string;
  key: string;
  name: string;
  size: number;
  type: string;
  uploaded_at: string;
  patient_id?: number;
  created_by?: number;
  created_by_name?: string;
  updated_at?: string;
  updated_by?: number;
  updated_by_name?: string;
  publicId?: string;
  thumbnail_url?: string;
  thumbnail_key?: string;
  created_at: string;
  category?: string;
}

export interface PresignedUrlResponse {
  data: PresignedUrlResponse | PromiseLike<PresignedUrlResponse>;
  uploadUrl: string;
  key: string;
  expiresIn: number;
}

export interface UploadedImageData {
  url: string;
  key: string;
  name: string;
  size: number;
  type: string;
  patient_id?: number;
  thumbnail_url?: string;
  thumbnail_key?: string;
  category?: string;
}

export class ImageService {
  private readonly BASE_PATH = '/images';
  protected readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  protected readonly ALLOWED_TYPES = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
  ];

  protected validateImage(file: File): void {
    if (!this.ALLOWED_TYPES.includes(file.type)) {
      throw new Error(
        'Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.'
      );
    }

    if (file.size > this.MAX_FILE_SIZE) {
      throw new Error('File size exceeds the 10MB limit.');
    }
  }

  /**
   * Get presigned URL from backend for direct S3 upload
   */
  async getPresignedUrl(
    fileName: string,
    fileType: string,
    patientId?: number,
    isThumbnail: boolean = false,
    category?: string
  ): Promise<PresignedUrlResponse> {
    this.validateImage({ name: fileName, type: fileType, size: 0 } as File);

    const response = await api.post<PresignedUrlResponse>(
      `${this.BASE_PATH}/upload-url`,
      {
        fileName,
        fileType,
        patientId,
        isThumbnail,
        category,
      }
    );

    return response.data;
  }

  // Upload directly to storage with progress tracking
  async uploadToStorage(
    uploadUrl: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<Response> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      

      xhr.upload.addEventListener('progress', event => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded * 100) / event.total);
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(
            new Response(xhr.response, {
              status: xhr.status,
              statusText: xhr.statusText,
            })
          );
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload aborted'));
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  }

  /**
   * Complete image upload flow with thumbnail:
   * 1. Generate thumbnail
   * 2. Get presigned URLs for both main image and thumbnail
   * 3. Upload both files directly to storage
   * 4. Return image data for database creation
   */
  async uploadImageDirectly(
    file: File,
    patientId?: number,
    onProgress?: (progress: number) => void,
    category?: string
  ): Promise<UploadedImageData> {
    this.validateImage(file);

    try {
      // Step 1: Generate thumbnail with higher quality
      const thumbnail = await generateThumbnail(file, {
        width: 500,
        height: 500,
        quality: 1,
        format: 'jpeg',
        maintainAspectRatio: true,
      });

      // Create thumbnail file
      const thumbnailFileName = generateThumbnailFilename(file.name, 'jpg');
      const thumbnailFile = new File([thumbnail.blob], thumbnailFileName, {
        type: 'image/jpeg',
      });

      // Step 2: Get presigned URLs from backend
      const [mainImageUrl, thumbnailUrl] = await Promise.all([
        this.getPresignedUrl(file.name, file.type, patientId, false, category),
        this.getPresignedUrl(
          thumbnailFileName,
          'image/jpeg',
          patientId,
          true,
          category
        ),
      ]);

      // Step 3: Upload both files directly to storage
      const [mainUploadResponse, thumbnailUploadResponse] = await Promise.all([
        this.uploadToStorage(mainImageUrl.uploadUrl, file, onProgress),
        this.uploadToStorage(thumbnailUrl.uploadUrl, thumbnailFile),
      ]);

      if (!mainUploadResponse.ok) {
        throw new Error('Failed to upload main image');
      }

      if (!thumbnailUploadResponse.ok) {
        throw new Error('Failed to upload thumbnail');
      }

      // Step 4: Return image data for database creation
      const imageData: UploadedImageData = {
        url: mainImageUrl.uploadUrl.split('?')[0], // Remove query parameters for clean URL
        key: mainImageUrl.key,
        name: file.name,
        size: file.size,
        type: file.type,
        patient_id: patientId,
        thumbnail_url: thumbnailUrl.uploadUrl.split('?')[0], // Remove query parameters for clean URL
        thumbnail_key: thumbnailUrl.key,
        category: category,
      };

      // Clean up thumbnail blob URL
      URL.revokeObjectURL(thumbnail.url);

      return imageData;
    } catch (error) {
      console.error('❌ Error in image upload process:', error);
      throw error;
    }
  }


}

export const imageService = new ImageService();
